import { DateFormat, FAIconName, FireflyKebabMenuItem } from '@equip.health/ui';
import { DateTime } from 'luxon';

import { SMTSummaryEntry } from '~/store/services/SMT';

interface SMTEntryKebabMenuHandlers {
  onAddComment: (entryId: string) => void;
  onDelete: (entryId: string) => void;
  onEdit: (entryId: string, entry?: SMTEntry) => void;
}

const dateFormat = DateFormat.shortYearMonthDay;
const startDateOffset = 6;

const formatSMTDate = (dateString: string) =>
  DateTime.fromFormat(dateString, dateFormat).toLocaleString({
    day: 'numeric',
    month: 'long',
  });

const formatSMTDay = (dateString: string) =>
  DateTime.fromFormat(dateString, dateFormat).toLocaleString({
    weekday: 'long',
  });

/**
 * Sums all entryCount values from the SMT summary entries array.
 * @param entries - Array of SMT summary entries
 * @returns The sum of all entry counts, or 0 if array is empty
 */
const sumEntryCounts = (entries: SMTSummaryEntry[]): number => {
  if (!entries || entries.length === 0) {
    return 0;
  }
  return entries.reduce((sum, entry) => sum + entry.entryCount, 0);
};

/**
 * Calculates the average entry count from SMT summary entries.
 * Rounds to 1 decimal place, or returns a whole number if the result is a clean whole number.
 * @param entries - Array of SMT summary entries
 * @returns The average entry count, rounded to 1 decimal (or whole number if clean), or 0 if array is empty
 */
const calculateAverageEntryCount = (entries: SMTSummaryEntry[]): number => {
  if (!entries || entries.length === 0) {
    return 0;
  }
  const sum = sumEntryCounts(entries);
  const average = sum / entries.length;
  const rounded = Math.round(average * 10) / 10;
  return rounded;
};

/**
 * Calculates the week date range (7 days including today, going back 6 days).
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 */
const getWeekDateRange = (): { endDate: string; startDate: string } => {
  const endDate = DateTime.now().toISODate()!;
  const startDate = DateTime.now()
    .minus({ days: startDateOffset })
    .toISODate()!;
  return { endDate, startDate };
};

/**
 * Gets the current month and previous month in YYYY-MM format.
 * @returns Object with currentMonth and previousMonth strings
 */
const getCurrentAndPreviousMonth = (): {
  currentMonth: string;
  previousMonth: string;
} => {
  const currentMonth = DateTime.now().toFormat(DateFormat.shortYearMonth);
  const previousMonth = DateTime.now()
    .minus({ months: 1 })
    .toFormat(DateFormat.shortYearMonth);
  return { currentMonth, previousMonth };
};

/**
 * Gets the date range (start and end) for a given month key in YYYY-MM format.
 * @param monthKey - Month string in YYYY-MM format (e.g., "2025-09")
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 */
const getMonthDateRange = (
  monthKey: string,
): {
  endDate: string;
  startDate: string;
} => {
  const monthStart = DateTime.fromFormat(
    monthKey,
    DateFormat.shortYearMonth,
  ).startOf('month');
  const monthEnd = monthStart.endOf('month');
  return {
    endDate: monthEnd.toISODate()!,
    startDate: monthStart.toISODate()!,
  };
};

/**
 * Creates kebab menu options for an SMT entry
 * @param entryId - The ID of the SMT entry
 * @param handlers - Object containing handler functions for each menu action
 * @param entry - Optional entry object to pass to the edit handler
 * @returns Array of FireflyKebabMenuItem configurations
 */
const createSMTEntryKebabMenuOptions = (
  entryId: string,
  handlers: SMTEntryKebabMenuHandlers,
  entry?: SMTEntry,
): FireflyKebabMenuItem[] => [
  {
    label: 'Edit entry',
    leftIcon: FAIconName.penRegular,
    leftIconColor: 'neutral.primary',
    onClick: () => handlers.onEdit(entryId, entry),
  },
  {
    label: 'Add comment',
    leftIcon: FAIconName.commentPlusRegular,
    leftIconColor: 'neutral.primary',
    onClick: () => handlers.onAddComment(entryId),
  },
  {
    label: 'Delete entry',
    leftIcon: FAIconName.trashRegular,
    leftIconColor: 'critical.base',
    onClick: () => handlers.onDelete(entryId),
  },
];

/**
 * Fills in missing dates in daily logs data with empty log entries.
 * For any date in the range that doesn't have a log, creates an empty DailyLog object.
 * @param dailyLogs - Array of daily logs from the API (may have gaps)
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns Complete array of daily logs with empty entries for missing dates, sorted by date (newest first)
 */
const populateMissingDailyLogs = (
  dailyLogs: DailyLog[] | undefined,
  startDate: string,
  endDate: string,
): DailyLog[] => {
  if (!dailyLogs) {
    dailyLogs = [];
  }

  // Create a map of existing logs by date for quick lookup
  const logsMap = new Map<string, DailyLog>();
  dailyLogs.forEach((log) => {
    logsMap.set(log.logDate, log);
  });

  // Generate all dates in the range (newest first to avoid sorting)
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);
  const allLogs: DailyLog[] = [];

  // Build array from end to start for descending order
  let currentDate = end;
  while (currentDate >= start) {
    const dateStr = currentDate.toISODate()!;

    // Use existing log or create empty one
    allLogs.push(
      logsMap.get(dateStr) ?? {
        id: `empty-${dateStr}`,
        logDate: dateStr,
        smtEntries: [],
      },
    );

    currentDate = currentDate.minus({ days: 1 });
  }

  return allLogs;
};

export {
  calculateAverageEntryCount,
  createSMTEntryKebabMenuOptions,
  formatSMTDate,
  formatSMTDay,
  getCurrentAndPreviousMonth,
  getMonthDateRange,
  getWeekDateRange,
  populateMissingDailyLogs,
  sumEntryCounts,
};

export type { SMTEntryKebabMenuHandlers };
